/**
 * Service to preload order page APIs on the home page
 * This improves order page load performance by caching data
 */

import { getAllTestsAndDiscountPanelsApi } from "@/api/va-order-page/pricing/item/price";
import { getSampleTypeSetsNew } from "@/utils/va-order-test/sampleTypes";
import { getTestTree } from "./testTree/testTree";
import { bfsSearch } from "./testTree/bfsSearch";
import { serializeTreeNodes, storeTreeData, retrieveTreeData, clearTreeData } from "./testTree/treeSerializer";
import _ from "lodash";

const CACHE_KEY_PREFIX = "va_portal_preload_";
const CACHE_VERSION = "v1"; // Increment this when data structure changes

/**
 * Convert sampleTypeSets from Sets to arrays for serialization
 */
function convertSetsToArrays(sampleTypeSets) {
  if (!sampleTypeSets) return null;
  return {
    blood: Array.from(sampleTypeSets.blood || []),
    nonBlood: Array.from(sampleTypeSets.nonBlood || []),
    dryIce: Array.from(sampleTypeSets.dryIce || [])
  };
}

/**
 * Convert sampleTypeSets from arrays to Sets after deserialization
 */
export function convertArraysToSets(sampleTypeSets) {
  if (!sampleTypeSets) return null;
  return {
    blood: new Set(Array.isArray(sampleTypeSets.blood) ? sampleTypeSets.blood : []),
    nonBlood: new Set(Array.isArray(sampleTypeSets.nonBlood) ? sampleTypeSets.nonBlood : []),
    dryIce: new Set(Array.isArray(sampleTypeSets.dryIce) ? sampleTypeSets.dryIce : [])
  };
}

/**
 * Get cache key for a specific currency
 */
function getCacheKey(currencyName = "usd") {
  return `${CACHE_KEY_PREFIX}${CACHE_VERSION}_${currencyName}`;
}

/**
 * Get cache timestamp key
 */
function getCacheTimestampKey(currencyName = "usd") {
  return `${CACHE_KEY_PREFIX}timestamp_${currencyName}`;
}

/**
 * Check if cached data is still valid (within 1 hour)
 */
function isCacheValid(currencyName = "usd") {
  const timestampKey = getCacheTimestampKey(currencyName);
  const timestamp = sessionStorage.getItem(timestampKey);
  
  if (!timestamp) {
    return false;
  }
  
  const cacheAge = Date.now() - parseInt(timestamp);
  const ONE_HOUR = 60 * 60 * 1000;
  
  return cacheAge < ONE_HOUR;
}

/**
 * Process raw API data into tree format
 */
function processApiData(data) {
  const dbsMappings = data.atHomeMappings || [];
  let { vaTests, vwTests, discountPanels, exceptions, dpCategory, vwTestCategory, regenere, nutriproZ } = data;
  // Handle undefined: set to empty array if backend not given
  regenere = regenere || [];
  let nutriproz = nutriproZ || [];

  // Process VA tests
  vaTests.forEach((test) => {
    test.isSelect = false;
    test.isSelectForShortcut = false;
    test.isSelectForFavorite = false;
    test.category = "va";
    test.isSelectForXChooseY = false;
    test.isNotAllowedSelect = false;
    test.isNotAllowedSelectForShortcut = false;
    test.isNotAllowedSelectForFavorite = false;
    test.isNotAllowedSelectForXChooseY = false;
    test.isException = false;
    test.sampleTypeSets = getSampleTypeSetsNew(test);
    test.originalTestName = test.testName;
    test.testName = test.displayName ? test.displayName : test.testName;
    test.originalPrice = test.price;
    test.sampleTypeSets = convertSetsToArrays(test.sampleTypeSets);
  });

  // Create tree structure for VA tests
  let vaTestTree = getTestTree(vaTests);
  
  // Order the root list
  let order = [1, 6, 18, 21, 22, 99, 198, 728, 14, 98, 23, 24, 199, 201, 65, 727, 51, 118, 7, 732, 710, 202];
  let vaRootNodeList = _.sortBy(vaTestTree, function(node) {
    return _.indexOf(order, node.id);
  });

  // Get BFS search order array from tree structure
  let vaNodeList = [];
  vaRootNodeList.forEach(root => {
    bfsSearch(root, vaNodeList, 0);
  });

  // Process VW tests
  vwTests.forEach((test) => {
    test.isSelect = false;
    test.isSelectForShortcut = false;
    test.isSelectForFavorite = false;
    test.category = "vw";
    test.isSelectForXChooseY = false;
    test.isNotAllowedSelect = false;
    test.isNotAllowedSelectForShortcut = false;
    test.isNotAllowedSelectForFavorite = false;
    test.isNotAllowedSelectForXChooseY = false;
    test.isException = false;
    test.sampleTypeSets = getSampleTypeSetsNew(test);
    test.originalTestName = test.testName;
    test.testName = test.displayName ? test.displayName : test.testName;
    test.originalPrice = test.price;
    test.sampleTypeSets = convertSetsToArrays(test.sampleTypeSets);
  });

  let vwRootNodeList = getTestTree(vwTests);
  let vwNodeList = [];
  vwRootNodeList.forEach(root => {
    bfsSearch(root, vwNodeList, 0);
  });

  // Process discount panels
  discountPanels.forEach(panel => {
    panel.isSelect = false;
    panel.testName = panel.bundleName;
    panel.isSelectForShortcut = false;
    panel.category = panel.quantity ? "xChooseY" : "discountPanel";
    panel.isException = false;
    panel.sampleTypeSets = getSampleTypeSetsNew(panel);
    if (panel.quantity) {
      panel.isOpenXChooseYDialog = false;
    }
    let { testList, packageList } = panel.bundleContent;
    let testIdList = testList ? testList.map(t => t.testId) : [];
    let packageIdList = packageList ? packageList.map(p => p.packageId) : [];
    panel.testIdsInPanel = [...new Set(testIdList.concat(packageIdList))];
    // add supplement test ids in testIdsInPanel for xChooseY panel
    if (panel.quantity && panel.supplementContent) {
      let { itemIdList } = panel.supplementContent;
      panel.testIdsInPanel = [...new Set(panel.testIdsInPanel.concat(itemIdList))];
    }
    panel.sampleTypeSets = convertSetsToArrays(panel.sampleTypeSets);
  });

  return {
    vaRootNodeList,
    vaNodeList,
    vwRootNodeList,
    vwNodeList,
    discountPanelList: discountPanels,
    exceptions,
    discountPanelCategory: dpCategory,
    vwTestCategory,
    dbsMappings,
    regenere,
    nutriproz
  };
}

/**
 * Preload and cache API data
 * @param {string} currencyName - Currency name (default: "usd")
 * @param {boolean} isAllTestsWithTubeInfo - Include tube info
 * @returns {Promise<boolean>} Success status
 */
export async function preloadOrderPageData(currencyName = "usd", isAllTestsWithTubeInfo = false) {
  try {
    // Check if valid cache exists
    if (isCacheValid(currencyName)) {
      return true;
    }
    
    // Fetch data from API
    const { data } = await getAllTestsAndDiscountPanelsApi(currencyName, isAllTestsWithTubeInfo);
    
    // Process the data
    const processedData = processApiData(data);

    // Serialize tree structures
    const vaTreeData = serializeTreeNodes(processedData.vaRootNodeList, processedData.vaNodeList);
    const vwTreeData = serializeTreeNodes(processedData.vwRootNodeList, processedData.vwNodeList);

    // Prepare cache object
    const cacheData = {
      vaTreeData,
      vwTreeData,
      discountPanelList: processedData.discountPanelList,
      exceptions: processedData.exceptions,
      discountPanelCategory: processedData.discountPanelCategory,
      vwTestCategory: processedData.vwTestCategory,
      dbsMappings: processedData.dbsMappings,
      regenere: processedData.regenere || [],
      nutriproz: processedData.nutriproz || [],
      isAllTestsWithTubeInfo
    };

    // Store in sessionStorage
    const cacheKey = getCacheKey(currencyName);
    const timestampKey = getCacheTimestampKey(currencyName);
    
    storeTreeData(cacheKey, cacheData, true);
    sessionStorage.setItem(timestampKey, Date.now().toString());
    
    return true;
  } catch (error) {
    console.error("[Preload] Error preloading data:", error);
    return false;
  }
}

/**
 * Retrieve cached data
 * @param {string} currencyName - Currency name (default: "usd")
 * @returns {Object|null} Cached data or null
 */
export function getCachedOrderPageData(currencyName = "usd") {
  try {
    if (!isCacheValid(currencyName)) {
      return null;
    }

    const cacheKey = getCacheKey(currencyName);
    const cachedData = retrieveTreeData(cacheKey, true);
    
    if (!cachedData) {
      return null;
    }

    return cachedData;
  } catch (error) {
    console.error("[Preload] Error retrieving cached data:", error);
    return null;
  }
}

/**
 * Clear all cached data
 */
export function clearAllCachedData() {
  try {
    const currencies = ["usd", "eur", "gbp", "cad"];
    currencies.forEach(currency => {
      clearTreeData(getCacheKey(currency), true);
      sessionStorage.removeItem(getCacheTimestampKey(currency));
    });
  } catch (error) {
    console.error("[Preload] Error clearing cached data:", error);
  }
}